import {
  Grid,
  MetricItem,
  space,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { t } from "i18next";
import type { FC } from "react";
import styledComponents from "styled-components";

import type { FunctionGridProps } from "../../partials/function-grid.tsx";
import {
  formatValue,
  getExpectedValue,
  isChannelOutdated,
} from "../../util/channel-data.tsx";
import type { Channel, Incident } from "../../view-model.ts";
import { getIncidentForChannel } from "../common/function-grid-helpers.tsx";

const StyledGrid = styledComponents(Grid)`
  // grid-template-rows: auto 1fr 1fr;
  height: 100%;
`;

const StyledGroupContainer = styledComponents.div`
  border: 1px solid ${theme.colorPalette.grey200};
  border-radius: ${theme.borderRadius.default};
  padding: ${space(4)} ${space(2)} ${space(2)} ${space(2)};
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledGroupHeader = styledComponents(Typography)`
  position: absolute;
  top: 0;
  right: ${space(4)};
  transform: translateY(-50%);
  background: #fff;
  padding: 0 ${space(3)};
  z-index: 1;
`;

const StyledGroupColumn = styledComponents(Grid.Column)`
  grid-row: span 2;
`;

const StyledInnerGrid = styledComponents(Grid)`
  flex: 1;
  grid-template-rows: 1fr 1fr;
  grid-row-gap: ${space(2)};
  grid-column-gap: 0;
  height: 100%;
`;

const FUMEHOOD_MAIN_FUNCS = new Set([
  "OperationMode",
  "Light",
  "Airflow",
  "Sash",
  "FrontSash",
  "HorizontalSash",
]);

const FUMEHOOD_CONTAINER_FUNCS = new Set([
  "SolventContainer1",
  "SolventContainer2",
]);

const FUMEHOOD_INTERNAL_FUNCS = new Set(["FlapAngle", "Temperature"]);

/** Map LADS BrowseNames to i18n translation keys */
const BROWSE_NAME_TO_I18N: Record<string, string> = {
  OperationMode: "channel.operation_status",
  Light: "channel.light",
  Airflow: "channel.airflow",
  FrontSash: "channel.sash_position",
  Sash: "channel.sash_position",
  HorizontalSash: "channel.cross_slide_position",
  FlapAngle: "channel.angle_flap",
  Temperature: "channel.temperature",
  SolventContainer1: "channel.solvent_container_1",
  SolventContainer2: "channel.solvent_container_2",
};

/** Extract the browseName (last segment) from a channel ID like "device/unit/BrowseName" */
function getBrowseName(channel: Channel): string {
  const lastSlash = channel.id.lastIndexOf("/");
  return lastSlash >= 0 ? channel.id.slice(lastSlash + 1) : channel.id;
}

function getTranslatedName(channel: Channel): string {
  const browseName = getBrowseName(channel);
  const key = BROWSE_NAME_TO_I18N[browseName];
  return key ? t(key) : channel.name;
}

export const FunctionGrid: FC<FunctionGridProps> = ({
  functions,
  incidents,
  onSelect,
}) => {
  const mainFunctions = functions.filter((f) =>
    FUMEHOOD_MAIN_FUNCS.has(getBrowseName(f))
  );
  const containerFunctions = functions.filter((f) =>
    FUMEHOOD_CONTAINER_FUNCS.has(getBrowseName(f))
  );
  const internalFunctions = functions.filter((f) =>
    FUMEHOOD_INTERNAL_FUNCS.has(getBrowseName(f))
  );
  return (
    <StyledGrid rowGap={space(3)}>
      <Grid.Column key="main" width={12}>
        <Grid>
          {mainFunctions.map((channel) =>
            renderFunction(channel, onSelect, 3, incidents)
          )}
        </Grid>
      </Grid.Column>
      <StyledGroupColumn key="container" width={6}>
        <StyledGroupContainer>
          <StyledGroupHeader>
            <Typography color="grey500" size="lg" weight="medium">
              {t("channel.container")}
            </Typography>
          </StyledGroupHeader>
          <StyledInnerGrid>
            {containerFunctions.map((channel) =>
              renderFunction(channel, onSelect, 6, incidents)
            )}
          </StyledInnerGrid>
        </StyledGroupContainer>
      </StyledGroupColumn>
      <StyledGroupColumn key="internal" width={6}>
        <StyledGroupContainer>
          <StyledGroupHeader>
            <Typography color="grey500" size="lg" weight="medium">
              {t("channel.internal")}
            </Typography>
          </StyledGroupHeader>
          <StyledInnerGrid>
            {internalFunctions.map((channel) =>
              renderFunction(channel, onSelect, 6, incidents)
            )}
          </StyledInnerGrid>
        </StyledGroupContainer>
      </StyledGroupColumn>
    </StyledGrid>
  );
};

function renderFunction(
  channel: Channel,
  onSelect: (c: Channel) => void,
  gridWidth = 3,
  incidents?: Incident[]
): JSX.Element {
  const incident = getIncidentForChannel(channel, incidents);
  return (
    <Grid.Column key={channel.id} width={gridWidth}>
      <div onClick={() => onSelect(channel)}>
        <MetricItem
          empty={!channel}
          expectedValue={getExpectedValue(channel)}
          incidentState={incident?.severity}
          outdated={isChannelOutdated(channel)}
          title={getTranslatedName(channel)}
          value={formatValue(
            channel.current?.value,
            channel.unit,
            channel.name
          )}
        />
      </div>
    </Grid.Column>
  );
}
